import {
  InformationCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import { BaseTag, Button, Tabs } from '@praetorian-chariot/ui';
import { FilePlus2 } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Accept } from 'react-dropzone/.';
import { toast } from 'sonner';

import { useTheme } from '@/app/ThemeContext';
import { Dropzone, Files } from '@/components/Dropzone';
import { Input } from '@/components/form/Input';
import { getFormValues, Inputs, Values } from '@/components/form/Inputs';
import { Link } from '@/components/Link';
import { Modal } from '@/components/Modal';
import { Container } from '@/components/ui';
import { useModifyAccount } from '@/hooks';
import { useUploadFile } from '@/hooks/useFiles';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import {
  ValidateIntegrationErrorResponse,
  ValidationCredentials,
} from '@/hooks/useValidateIntegration';
import { JiraProjectSearch } from '@/sections/JiraProjectSearch';
import {
  Account,
  IntegrationInput,
  IntegrationMeta,
  JiraProject,
  JiraTemplate,
  LinkAccount,
} from '@/types';
import { cn } from '@/utils/classname';
import { generateUuid } from '@/utils/uuid.util';

// Map vulnerability scanners to their accepted file types
const VULNERABILITY_SCANNERS: {
  [key: string]: { accept: Accept };
} = {
  nessus: { accept: { 'application/.nessus': ['.nessus'] } },
  qualys: { accept: { 'text/csv': ['.csv'] } },
  insightvm: { accept: { 'application/xml': ['.xml'] } },
  sharphound: { accept: { 'application/zip': ['.zip'] } },
  cisachvs: { accept: { 'text/csv': ['.csv'] } },
  cisachwas: { accept: { 'text/csv': ['.csv'] } },
  nsacsaasasm: { accept: { 'text/csv': ['.csv'] } },
} as const;

type Step = 'setup' | 'configuration' | 'permissions';

interface SetupModalProps {
  step?: Step;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  selectedIntegration?: Account;
  formData: Values;
  integration?: IntegrationMeta;
  hideAddLater?: boolean;
  parseFormData?: (formData: Values) => Values;
}

const configurationTabs = [
  { label: 'Configuration', value: 'configuration' },
  { label: 'Permissions', value: 'permissions' },
];

const SetupModal: React.FC<SetupModalProps> = ({
  step: stepProps = 'setup',
  isOpen,
  onClose,
  onComplete,
  selectedIntegration: connectedIntegration,
  formData: formDataParent,
  integration: integrationMeta,
  parseFormData,
}) => {
  const integration: IntegrationMeta | undefined =
    integrationMeta ||
    (connectedIntegration
      ? AllIntegrations[
          connectedIntegration.member as keyof typeof AllIntegrations
        ]
      : undefined);
  const shouldHandleToastsManually = Boolean(
    integration?.linkOnValidateFailure
  );

  const defaultSetupValues =
    integration?.inputs && formDataParent
      ? { ...getFormValues(integration.inputs), ...formDataParent }
      : undefined;

  const defaultConfigValuesWithoutId = integration?.configuration?.inputs
    ? {
        ...getFormValues(integration?.configuration?.inputs),
        ...formDataParent,
      }
    : undefined;

  const defaultConfigValues = defaultConfigValuesWithoutId
    ? {
        ...defaultConfigValuesWithoutId,
        id: defaultConfigValuesWithoutId.id || generateUuid(),
      }
    : undefined;

  const isVulnerabilityScanner =
    integration?.id && integration.id in VULNERABILITY_SCANNERS;
  const scannerConfig = isVulnerabilityScanner
    ? VULNERABILITY_SCANNERS[
        integration.id as keyof typeof VULNERABILITY_SCANNERS
      ]
    : null;
  const key = connectedIntegration?.key;

  // Mutations
  const { mutateAsync: link, status: linkStatus } = useModifyAccount(
    'link',
    undefined,
    shouldHandleToastsManually
  );
  const { mutateAsync: unLink, status: unLinkStatus } =
    useModifyAccount('unlink');
  const { mutateAsync: updateAccount, status: updateAccountStatus } =
    useModifyAccount('update');
  const { mutateAsync: uploadFile } = useUploadFile();
  const {
    projects,
    onValidateIntegration,
    templatesError,
    setTemplatesError,
    getIntegrationProjects,
    searchProjects,
    setProjects,
  } = useIntegrationSettings(
    integration?.overrideMemberName || integration?.id,
    connectedIntegration?.value
  );
  const { currentTheme } = useTheme();

  // Local States
  const [step, setStep] = useState<'setup' | 'configuration' | 'permissions'>(
    stepProps
  );
  const [setupData, setSetupData] = useState<Values>(defaultSetupValues || {});
  const [configData, setConfigData] = useState<Values>(
    defaultConfigValues || {}
  );
  const validationBypassedRef = useRef(false);

  // Memoized values
  const isEditingMode = useMemo(() => {
    return !!integration?.configuration && !!key;
  }, [integration?.configuration, key]);

  const {
    formErrors,
    setFormErrors,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
    validateForm,
  } = useFormValidation();

  // Wrapper for searchProjects that handles both new and existing integrations
  const handleSearchProjects = useCallback(
    async (searchTerm: string) => {
      // For existing integrations (has key), searchProjects uses the account key
      // For new integrations (no key), pass the credentials from setupData
      const config = key ? undefined : convertObjectValuesToStrings(setupData);
      return searchProjects(searchTerm, config);
    },
    [key, setupData, searchProjects]
  );

  useEffect(() => {
    if (step === 'configuration') {
      if (key) {
        // Existing integration - load projects using account key
        getIntegrationProjects();
      } else if (Object.keys(setupData).length > 0) {
        // New integration - load projects using credentials
        handleSearchProjects('').then(initialProjects => {
          setProjects(initialProjects);
        });
      }
    }
  }, [
    key,
    step,
    getIntegrationProjects,
    handleSearchProjects,
    setupData,
    setProjects,
  ]);

  const handleSetupInputsChange = (newValues: Values) => {
    setSetupData(newValues);

    // If user has attempted to submit, validate on change
    if (hasAttemptedSubmit) {
      const newErrors: Record<string, boolean> = { ...formErrors };
      Object.keys(newValues).forEach(key => {
        if (newValues[key]) {
          delete newErrors[key];
        }
      });
      setFormErrors(newErrors);
    }
  };

  const handleConfigDataChange = (newValues: Values) => {
    setFormErrors({});
    setTemplatesError({});
    setConfigData(currentValues => {
      // Preserve projectKey if newValues has empty/default value (from Inputs component)
      // Only update projectKey if newValues has a non-empty value (from JiraProjectSearch)
      const projectKey = newValues.projectKey || currentValues.projectKey;
      const merged = { ...currentValues, ...newValues, projectKey };
      const projectKeyChanged = currentValues.projectKey !== projectKey;

      if (projectKeyChanged) {
        return { ...merged, issueType: '' };
      }
      return merged;
    });
  };

  // const handleSetupLater = async () => {
  //   const hasInputs = Object.keys(integration || {}).includes('inputs');

  //   await link({
  //     username: integration?.id || '',
  //     value: hasInputs ? 'setup' : 'waitlisted',
  //     config: {},
  //   });

  //   onComplete();
  //   onClose();
  // };

  const handleFileDrop = async (files: Files<'arrayBuffer'>) => {
    if (!integration?.id) return;

    try {
      const { file, content } = files[0];
      const fileUploadName = `imports/${integration.id}/${file.name}`;

      await uploadFile({
        name: fileUploadName,
        content,
      });

      await link({
        value: fileUploadName,
        config: {},
        username: `${integration.id}-import`,
      });

      onComplete();
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const validateIntegrationConfig = async (preparedFormData: LinkAccount) => {
    validationBypassedRef.current = false;

    if (!integration?.validate && !integration?.configuration) return true;

    const { isValid, templatesError: templatesErrorResponse } =
      await onValidateIntegration(
        {
          account: preparedFormData.key ?? '',
          config: preparedFormData.config,
          settings: preparedFormData.settings,
          templateId:
            step === 'configuration' ? String(configData.id) : undefined,
        },
        {
          suppressErrorToast: shouldHandleToastsManually,
        }
      );

    const templateError = configData.id
      ? templatesErrorResponse?.[configData.id]
      : undefined;

    if (step === 'configuration' && templateError) {
      setStep('permissions');
      return false;
    }

    if (templateError) return false;

    if (!isValid) {
      const canBypass = Boolean(integration?.linkOnValidateFailure);
      validationBypassedRef.current = canBypass;
      return canBypass;
    }

    if (step === 'setup' && isValid && integration.configuration) {
      setStep('configuration');
      return false;
    }

    return true;
  };

  const prepareFormData = (): LinkAccount => {
    const setupDataRequestObj = parseFormData
      ? parseFormData(setupData)
      : setupData;
    const { displayName, ...restConfig } = configData;

    const projectName = projects.find(
      ({ key }) => key === restConfig.projectKey
    )?.name;

    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const currentTemplate = {
      ...restConfig,
      projectName,
    };

    const hasMatchingTemplate = existingTemplates.some(
      template => template.id === restConfig.id
    );
    const newTemplates = isEditingMode
      ? hasMatchingTemplate
        ? existingTemplates.map(template =>
            template.id === restConfig.id ? currentTemplate : template
          )
        : [...existingTemplates, currentTemplate]
      : [...existingTemplates, currentTemplate];

    const result = {
      username: integration?.overrideMemberName || integration?.id || '',
      value: String(setupDataRequestObj.value),
      config: convertObjectValuesToStrings(setupDataRequestObj),
      key,
      settings:
        step === 'configuration'
          ? {
              displayName: String(displayName),
              templates: (newTemplates as JiraTemplate[]).map(value =>
                convertObjectValuesToStrings(value)
              ),
            }
          : setupDataRequestObj.severities
            ? { severityThreshold: String(setupDataRequestObj.severities) }
            : undefined,
    };

    return result;
  };

  const handleLinkUnlinkIntegration = async (integrationData: LinkAccount) => {
    if (
      connectedIntegration &&
      !integration?.multiple &&
      !integration?.configuration
    ) {
      await unLink({
        username: connectedIntegration.member,
        value: connectedIntegration.value,
        config: connectedIntegration.config,
      });
    }
    if (isEditingMode) {
      await updateAccount(integrationData);
    } else {
      await link(integrationData);
    }
    onComplete();
    onClose();
  };

  const handleLinkIntegration = async () => {
    setHasAttemptedSubmit(true);
    const inputs =
      step === 'setup'
        ? integration?.inputs
        : integration?.configuration?.inputs;

    // Client side validation
    if (!validateForm(inputs, step === 'setup' ? setupData : configData)) {
      toast.error('Form validation failed', {
        description: 'Please fill in all required fields',
      });
      return;
    }

    try {
      const integrationData = prepareFormData();
      if (!(await validateIntegrationConfig(integrationData))) {
        return;
      }
      await handleLinkUnlinkIntegration(integrationData as LinkAccount);

      if (shouldHandleToastsManually) {
        if (validationBypassedRef.current) {
          toast.warning(
            'Unable to verify the credentials, but the integration has been added'
          );
        } else {
          toast.success(
            isEditingMode ? 'Integration updated' : 'Integration connected',
            {
              description: isEditingMode
                ? 'Your changes have been saved successfully.'
                : 'Your integration has been successfully connected.',
            }
          );
        }
        validationBypassedRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to update integration', {
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
      });
    }
  };

  if (!integration) return null;

  // Map inputs to include error state
  const inputsWithErrors =
    integration.inputs?.map(input => ({
      ...input,
      error: formErrors[input.name] ? ' ' : undefined, // Using space to trigger error state without showing message
    })) || [];

  const getModalFooterConfig = () => {
    return {
      hideCancelButton: false,
      text:
        step === 'permissions' && Object.keys(templatesError).length > 0
          ? 'Try Again'
          : isEditingMode
            ? 'Save'
            : 'Connect',
      onClick:
        step === 'permissions' && Object.keys(templatesError).length > 0
          ? () => setStep('configuration')
          : handleLinkIntegration,
      disabled: [unLinkStatus, linkStatus, updateAccountStatus].includes(
        'pending'
      ),
      className: 'px-6',
    };
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex w-full flex-row justify-between">
          <p>
            {integration.name} {step === 'setup' ? 'Setup' : 'Configuration'}
          </p>
          <img
            src={`/icons/${currentTheme}/${integration.logo}`}
            alt={integration.name}
            className="size-6"
          />
        </div>
      }
      hideFooter={integration.hideFooter}
      footer={getModalFooterConfig()}
    >
      {step === 'setup' && (
        <SetupSection
          integration={integration}
          inputsWithErrors={inputsWithErrors}
          setupData={setupData}
          onComplete={onComplete}
          defaultSetupValues={defaultSetupValues}
          handleSetupInputsChange={handleSetupInputsChange}
          handleFileDrop={handleFileDrop}
          isVulnerabilityScanner={isVulnerabilityScanner}
          scannerConfig={scannerConfig}
          onClose={onClose}
        />
      )}

      {(step === 'configuration' || step === 'permissions') && (
        <ConfigurationSection
          step={step}
          setStep={setStep}
          integration={integration}
          templatesError={templatesError}
          projects={projects}
          formErrors={formErrors}
          configData={configData}
          defaultConfigValues={defaultConfigValues}
          handleConfigDataChange={handleConfigDataChange}
          searchProjects={handleSearchProjects}
          setProjects={setProjects}
        />
      )}
    </Modal>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook exported alongside components for convenience
export const useFormValidation = () => {
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const validateInputField = (
    input: IntegrationInput,
    value: string | number
  ): boolean => {
    if (input.required && !value) return false;
    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      return regex.test(value as string);
    }
    return true;
  };

  const validateForm = (
    inputs: IntegrationMeta['inputs'],
    data: Values
  ): boolean => {
    if (!inputs) return true;

    const newErrors: Record<string, boolean> = {};
    let isValid = true;

    (inputs || []).forEach(input => {
      if (!validateInputField(input, data[input.name])) {
        newErrors[input.name] = true;
        isValid = false;
      }
    });

    setFormErrors(newErrors);
    return isValid;
  };

  return {
    formErrors,
    setFormErrors,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
    validateForm,
  };
};

interface SetupSectionProps {
  integration: IntegrationMeta;
  onComplete: () => void;
  inputsWithErrors: IntegrationInput[];
  setupData: Values;
  defaultSetupValues?: Values;
  handleSetupInputsChange: (newValues: Values) => void;
  handleFileDrop: (files: Files<'arrayBuffer'>) => void;
  isVulnerabilityScanner?: boolean;
  scannerConfig: { accept: Accept } | null;
  onClose: () => void;
}

export const SetupSection: React.FC<SetupSectionProps> = ({
  integration,
  onComplete,
  inputsWithErrors,
  setupData,
  defaultSetupValues,
  handleSetupInputsChange,
  handleFileDrop,
  isVulnerabilityScanner,
  scannerConfig,
  onClose,
}) => {
  return (
    <>
      {integration.help && (
        <div className="bg-chariot-background-secondary rounded-lg px-4 py-2">
          <p className="mb-1 text-sm font-bold">Need help?</p>
          <div className="flex flex-col space-y-2">
            <Link
              styleType="text"
              to={integration.help.href}
              target="_blank"
              rel="noopener noreferrer"
              buttonClass="p-0 hover:underline text-chariot-text-secondary font-base"
            >
              <InformationCircleIcon className="size-5" />
              <span className="underline">{integration.help.label}</span>
            </Link>
          </div>
        </div>
      )}

      {integration.Markup && (
        <div className="mt-4">
          <integration.Markup onComplete={onComplete} onClose={onClose} />
        </div>
      )}

      <div className="mt-4 space-y-4">
        <Inputs
          inputs={inputsWithErrors}
          onChange={handleSetupInputsChange}
          values={setupData}
          defaultValues={defaultSetupValues}
        />

        {isVulnerabilityScanner && scannerConfig && (
          <>
            {inputsWithErrors.length > 0 &&
              (integration.inputs?.length ?? 0) > 0 && (
                <div className="relative mt-8">
                  <div className="relative flex justify-center">
                    <span className="bg-chariot-background-primary px-8 text-center font-semibold">
                      Or
                    </span>
                  </div>
                </div>
              )}
            <Dropzone
              type="arrayBuffer"
              className="border-chariot-stroke bg-chariot-background-primary rounded-lg border-2 border-dashed p-2"
              onFilesDrop={handleFileDrop}
              accept={scannerConfig.accept}
            >
              <div className="flex flex-col items-center p-2">
                <FilePlus2 className="text-chariot-text-secondary size-8" />
                <h3 className="m-0 mt-2 p-0 text-center text-lg font-medium">
                  Import {integration.name} Data
                </h3>
                <p className="text-chariot-text-primary mb-3 text-center text-sm">
                  Drag & Drop or Choose scan results to import
                </p>
                <Button
                  styleType="rounded"
                  size="sm"
                  label="Choose Files"
                  className="text-md p-2 py-0.5"
                  startIcon={<PlusCircleIcon className="size-5" />}
                  onClick={() => {}}
                />
              </div>
            </Dropzone>
          </>
        )}
      </div>
    </>
  );
};

interface ConfigurationSectionProps {
  step: 'configuration' | 'permissions';
  setStep: (step: 'configuration' | 'permissions') => void;
  integration: IntegrationMeta;
  templatesError: ValidateIntegrationErrorResponse['templates'];
  formErrors: Record<string, boolean>;
  configData: Values;
  projects: JiraProject[];
  defaultConfigValues?: Values;
  handleConfigDataChange: (newValues: Values) => void;
  searchProjects: (searchTerm: string) => Promise<JiraProject[]>;
  setProjects: React.Dispatch<React.SetStateAction<JiraProject[]>>;
}

export const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  step,
  setStep,
  integration,
  configData,
  templatesError,
  formErrors,
  defaultConfigValues,
  handleConfigDataChange,
  projects,
  searchProjects,
  setProjects: _setProjects,
}) => {
  // Check if this is a Jira integration (has projectKey input)
  const isJiraIntegration = integration?.configuration?.inputs?.some(
    input => input.name === 'projectKey'
  );

  const getOptions = useCallback(
    (input: IntegrationInput, name: string) => {
      if (name === 'projectKey') {
        return projects.map(project => ({
          label: `${project.name} (${project.key})`,
          value: project.key,
        }));
      }
      if (name === 'issueType') {
        return projects
          .find(project => project.key === configData?.projectKey)
          ?.issueTypes.map(({ name }) => ({ label: name, value: name }));
      }
      return input.options;
    },
    [projects, configData]
  );

  const handleProjectKeyChange = (projectKey: string) => {
    handleConfigDataChange({
      ...configData,
      projectKey,
      issueType: '', // Reset issue type when project changes
    });
  };

  const getInputsConfiguration = useMemo(() => {
    const mapInputConfiguration = (
      input: IntegrationInput & {
        dependsOn?: string;
        hideTillDependsOn?: boolean;
      }
    ) => {
      const {
        dependsOn,
        hideTillDependsOn,
        hidden: hiddenInput,
        ...rest
      } = input;
      const options = getOptions(input, rest.name) || [];
      const disabled = dependsOn ? !configData[dependsOn] : false;
      const hidden = hideTillDependsOn ? disabled : hiddenInput;
      return {
        ...rest,
        disabled: dependsOn
          ? !configData[dependsOn]
          : rest.type === Input.Type.SELECT
            ? options.length === 0
            : false,
        hidden,
        options,
        error: formErrors[input.name] ? ' ' : undefined,
      };
    };

    // Filter out projectKey for Jira - it will be rendered separately
    const inputs = (integration?.configuration?.inputs || []).filter(
      input => !(isJiraIntegration && input.name === 'projectKey')
    );

    return (
      inputs.map(({ dependsOn, ...input }) =>
        mapInputConfiguration({ ...input, dependsOn })
      ) || []
    );
  }, [
    integration?.configuration?.inputs,
    configData,
    formErrors,
    getOptions,
    isJiraIntegration,
  ]);

  return (
    <>
      {(step === 'configuration' || step === 'permissions') &&
        integration.configuration?.permissions && (
          <Tabs
            tabs={configurationTabs}
            selected={configurationTabs.find(tab => tab.value === step) || null}
            onSelect={tab =>
              setStep(tab.value as 'configuration' | 'permissions')
            }
          />
        )}

      <Container
        className={cn(
          'mt-4 hidden space-y-4',
          step === 'configuration' && 'block'
        )}
      >
        {isJiraIntegration && (
          <div className="space-y-1">
            <label
              htmlFor="projectKey"
              className="text-chariot-text-primary block text-sm font-medium"
            >
              Project
            </label>
            <JiraProjectSearch
              value={configData.projectKey as string}
              onChange={handleProjectKeyChange}
              searchProjects={searchProjects}
              initialProjects={projects}
              error={formErrors.projectKey ? ' ' : undefined}
              placeholder="Search for a Jira project..."
            />
          </div>
        )}
        <Inputs
          inputs={getInputsConfiguration}
          onChange={handleConfigDataChange}
          values={configData}
          defaultValues={defaultConfigValues}
        />
      </Container>

      {step === 'permissions' && (
        <Container className="mt-4 space-y-4">
          {Object.values(integration.configuration?.permissions ?? {})?.map(
            ({ key, name, description, permission }) => {
              const havePermission = !(
                templatesError[configData.id]?.missingPermissions ?? []
              ).includes(key);
              return (
                <PermissionRow
                  key={key}
                  name={name}
                  description={description}
                  permission={permission}
                  havePermission={havePermission}
                />
              );
            }
          )}
        </Container>
      )}
    </>
  );
};

export const PermissionRow = ({
  name,
  description,
  permission,
  havePermission,
}: {
  name: string;
  description: string;
  permission: string;
  havePermission: boolean;
}) => {
  return (
    <div className="[&:not(:last-child)]:border-chariot-stroke flex items-center justify-between gap-2 pb-4 not-last:border-b">
      <div>
        <p
          className={cn(
            'text-sm font-bold',
            !havePermission && 'text-chariot-stroke-critical'
          )}
        >
          {name}
        </p>
        <p className="text-chariot-text-secondary text-xs">{description}</p>
      </div>
      <BaseTag
        className={cn(
          'h-6 px-2 py-1',
          !havePermission && 'text-chariot-stroke-critical'
        )}
      >
        {permission}
      </BaseTag>
    </div>
  );
};

const convertObjectValuesToStrings = (
  data: Record<string, string | number>
): ValidationCredentials =>
  Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {} as ValidationCredentials);

export default SetupModal;
